import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';
import { ArrowDropDown as ArrowDropDownIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import clienteService from '../../../services/clienteService';
import estrategiaService from '../../../services/estrategiaService';
import LocalidadSearch from '../../../components/LocalidadSearch';
import type { POSCliente } from './types';

const esConsumidorFinalIva = (tipo: string) => {
  const t = tipo.trim().toUpperCase();
  return t === 'C' || t === 'CF';
};

const TIPO_IVA_OPCIONES: { value: string; label: string }[] = [
  { value: 'I', label: 'Responsable Inscripto' },
  { value: 'C', label: 'Consumidor Final' },
  { value: 'M', label: 'Monotributo' },
  { value: 'E', label: 'Exento' },
  { value: 'N', label: 'No Categorizado' },
];

const extractApiError = (e: unknown): string => {
  const err = e as { response?: { data?: unknown }; message?: string };
  const data = err?.response?.data;
  if (!data) return err?.message ?? 'Error al guardar el cliente';
  if (typeof data === 'string') return data;
  const d = data as Record<string, unknown>;
  if (d.message) return String(d.message);
  if (d.errors && typeof d.errors === 'object') {
    return Object.values(d.errors as Record<string, string[]>)
      .flat()
      .join(' | ');
  }
  return JSON.stringify(data).substring(0, 300);
};

interface LocalidadSel {
  codigoCiudad: number;
  nombreCiudad?: string;
  nombreProvincia?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (cliente: POSCliente) => void;
}

const FacturaPOSNuevoClienteDialog: React.FC<Props> = ({ open, onClose, onCreated }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [saving, setSaving] = useState(false);
  const [loadingInit, setLoadingInit] = useState(false);
  const [nroCliente, setNroCliente] = useState(0);
  const [nroCuenta, setNroCuenta] = useState<number | null>(null);
  const [nombre, setNombre] = useState('');
  const [cuit, setCuit] = useState('');
  const [dni, setDni] = useState('');
  const [tipoIva, setTipoIva] = useState('C');
  const [direccion, setDireccion] = useState('');
  const [localidad, setLocalidad] = useState<LocalidadSel | null>(null);
  const [localidadOpen, setLocalidadOpen] = useState(false);
  const [telefono1, setTelefono1] = useState('');
  const [email, setEmail] = useState('');

  const resetForm = useCallback(() => {
    setNombre('');
    setCuit('');
    setDni('');
    setTipoIva('C');
    setDireccion('');
    setLocalidad(null);
    setTelefono1('');
    setEmail('');
  }, []);

  useEffect(() => {
    if (!open) return;
    resetForm();
    setLoadingInit(true);
    void (async () => {
      try {
        const [proximo, estrategia] = await Promise.all([
          clienteService.getProximoNumero(),
          estrategiaService.getEstrategia(1).catch(() => null),
        ]);
        setNroCliente(Number(proximo) || 0);
        const cuenta = estrategia?.clientes;
        setNroCuenta(cuenta != null && Number(cuenta) > 0 ? Number(cuenta) : null);
      } catch {
        enqueueSnackbar('No se pudo obtener el próximo número de cliente', { variant: 'warning' });
        setNroCliente(0);
        setNroCuenta(null);
      } finally {
        setLoadingInit(false);
      }
    })();
  }, [open, resetForm, enqueueSnackbar]);

  const handleSubmit = async () => {
    const nombreTrim = nombre.trim();
    if (!nombreTrim) {
      enqueueSnackbar('Ingresá el nombre del cliente', { variant: 'warning' });
      return;
    }
    if (!tipoIva.trim()) {
      enqueueSnackbar('Seleccioná el tipo de IVA', { variant: 'warning' });
      return;
    }
    if (!direccion.trim()) {
      enqueueSnackbar('Ingresá la dirección de facturación', { variant: 'warning' });
      return;
    }
    if (!localidad?.codigoCiudad || localidad.codigoCiudad <= 0) {
      enqueueSnackbar('Seleccioná la ciudad', { variant: 'warning' });
      return;
    }
    if (!nroCliente || nroCliente <= 0) {
      enqueueSnackbar('No hay número de cliente disponible. Reintentá o cargá el cliente desde el maestro.', {
        variant: 'warning',
      });
      return;
    }
    if (!nroCuenta || nroCuenta <= 0) {
      enqueueSnackbar(
        'No se encontró cuenta contable por defecto (estrategia). Configurá la estrategia o dé de alta el cliente desde Clientes.',
        { variant: 'warning' },
      );
      return;
    }

    const cuitDigits = cuit.replace(/\D/g, '');
    const dniNum = (() => {
      const s = dni.replace(/\D/g, '');
      if (!s) return null;
      const n = Number(s);
      return Number.isFinite(n) && n >= 0 ? n : null;
    })();

    const hoy = new Date().toISOString().split('T')[0];
    const tipoIvaNorm = tipoIva.trim().toUpperCase();
    const esCf = esConsumidorFinalIva(tipoIvaNorm);

    setSaving(true);
    try {
      const payload = {
        nroCliente,
        nombre: nombreTrim,
        razonSocial: nombreTrim,
        direccion: direccion.trim(),
        codigoCiudad: localidad.codigoCiudad,
        cuit: esCf ? null : (cuitDigits || null),
        dni: esCf ? dniNum : null,
        tipoIva: tipoIvaNorm,
        condVta: 'CONTADO',
        tipoCliente: 'A',
        nroCuenta,
        fechaAlta: hoy,
        vendedor: 1,
        nroCobrador: 1,
        codigoDireccion: 'FISCAL',
        codigoRuta: 1,
        deposito: 0,
        telefono1: telefono1.trim() || null,
        email: email.trim().toLowerCase() || null,
        credito: 0,
        porcDesc: 0,
        porcDesc2: 0,
        porcDesc3: 0,
      };

      const creado = await clienteService.createCliente(payload);
      const nro = Number(creado.nroCliente) || nroCliente;
      let completo = creado as POSCliente;
      try {
        completo = (await clienteService.getCliente(nro)) as POSCliente;
      } catch {
        /* usar respuesta del alta */
      }
      enqueueSnackbar(`Cliente #${nro} creado`, { variant: 'success' });
      onCreated(completo);
      onClose();
    } catch (e) {
      enqueueSnackbar(extractApiError(e), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const localidadLabel = localidad
    ? [localidad.nombreCiudad, localidad.nombreProvincia].filter(Boolean).join(', ')
    : '';

  const muestraDni = esConsumidorFinalIva(tipoIva);

  const handleTipoIvaChange = (value: string) => {
    setTipoIva(value);
    if (esConsumidorFinalIva(value)) {
      setCuit('');
    } else {
      setDni('');
    }
  };

  return (
    <>
      <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Nuevo cliente</DialogTitle>
        <DialogContent dividers>
          {loadingInit ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Grid container spacing={2} sx={{ pt: 0.5 }}>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Nº de cliente asignado: <strong>{nroCliente > 0 ? nroCliente : '—'}</strong>
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Nombre"
                  required
                  fullWidth
                  size="small"
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                  autoFocus
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Tipo IVA"
                  required
                  fullWidth
                  size="small"
                  value={tipoIva}
                  onChange={(e) => handleTipoIvaChange(e.target.value)}
                >
                  {TIPO_IVA_OPCIONES.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                {muestraDni ? (
                  <TextField
                    label="DNI"
                    fullWidth
                    size="small"
                    value={dni}
                    onChange={(e) => setDni(e.target.value)}
                  />
                ) : (
                  <TextField
                    label="CUIT"
                    fullWidth
                    size="small"
                    value={cuit}
                    onChange={(e) => setCuit(e.target.value)}
                  />
                )}
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Dirección (facturación)"
                  required
                  fullWidth
                  size="small"
                  value={direccion}
                  onChange={(e) => setDireccion(e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Ciudad"
                  required
                  fullWidth
                  size="small"
                  value={localidadLabel}
                  placeholder="Buscar localidad…"
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          color="primary"
                          onClick={() => setLocalidadOpen(true)}
                          aria-label="Buscar ciudad"
                        >
                          <ArrowDropDownIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Teléfono"
                  fullWidth
                  size="small"
                  value={telefono1}
                  onChange={(e) => setTelefono1(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email"
                  fullWidth
                  size="small"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={() => void handleSubmit()} disabled={saving || loadingInit}>
            {saving ? 'Guardando…' : 'Crear y seleccionar'}
          </Button>
        </DialogActions>
      </Dialog>

      <LocalidadSearch
        open={localidadOpen}
        onClose={() => setLocalidadOpen(false)}
        onSelect={(loc) => {
          setLocalidad({
            codigoCiudad: loc.codigoCiudad,
            nombreCiudad: loc.nombreCiudad,
            nombreProvincia: loc.nombreProvincia,
          });
          setLocalidadOpen(false);
        }}
      />
    </>
  );
};

export default FacturaPOSNuevoClienteDialog;
